import { useState } from 'react'
import ScrollReveal from '../components/marketing/ScrollReveal'
import { setTheme } from '../lib/theme'

// ─── Design tokens ───
const t = {
  bg: '#F5F2EC',
  bgDark: '#252019',
  text: '#1A1714',
  textBody: '#6B6560',
  textFaint: '#9E9189',
  accent: '#C4622A',
  accentBtn: '#D97645',
  badgeBg: '#F0DDD5',
  cardBg: '#FFFFFF',
  border: '#E0DCD4',
  fontMain: '"Geist", system-ui, sans-serif',
} as const

// ═══════════════════════════════════════════════
// REUSABLE COMPONENTS
// ═══════════════════════════════════════════════

function CornerLogo({ color = '#F5F2EC', size = 22 }: { color?: string; size?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <svg width={size} height={size} viewBox="0 0 256 256" fill="none">
        <defs>
          <linearGradient id="featLogoGrad" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
            <stop stopColor="#D46A3A"/>
            <stop offset="1" stopColor="#8B3A10"/>
          </linearGradient>
        </defs>
        <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#featLogoGrad)" strokeWidth="40" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: 16, fontWeight: 600, color, fontFamily: t.fontMain, letterSpacing: '-0.01em', lineHeight: '20px' }}>Corner</span>
    </div>
  )
}

function PrimaryButton({ children, href }: { children: string; href: string }) {
  return (
    <a href={href} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 8, padding: '12px 24px',
      backgroundColor: t.accentBtn, textDecoration: 'none',
    }}>
      <span style={{ fontSize: 15, fontWeight: 500, color: '#FFFFFF', fontFamily: t.fontMain, lineHeight: '18px' }}>{children}</span>
    </a>
  )
}

function OutlineButton({ children, href }: { children: string; href: string }) {
  return (
    <a href={href} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 8, padding: '12px 24px',
      border: `1px solid ${t.accentBtn}`, textDecoration: 'none',
    }}>
      <span style={{ fontSize: 15, fontWeight: 500, color: t.accentBtn, fontFamily: t.fontMain, lineHeight: '18px' }}>{children}</span>
    </a>
  )
}

// ═══════════════════════════════════════════════
// NAVBAR
// ═══════════════════════════════════════════════

function Navbar() {
  return (
    <nav style={{
      width: '100%', height: 60, display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', padding: '0 40px',
      position: 'sticky', top: 0, zIndex: 100,
      backgroundColor: '#F5F2ECE6', borderBottom: `1px solid ${t.border}`,
      backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    }}>
      <a href="/" style={{ textDecoration: 'none' }}>
        <CornerLogo color={t.text} size={22} />
      </a>
      <div style={{ display: 'flex', gap: 32 }}>
        {['Features', 'How it works', 'Pricing', 'FAQ'].map(link => (
          <a key={link} href={
            link === 'Features' ? '/features' :
            link === 'How it works' ? '/how-it-works' :
            link === 'Pricing' ? '/pricing' :
            `/#${link.toLowerCase().replace(/\s+/g, '-')}`
          } style={{
            fontSize: 14, fontWeight: 450, color: '#5C5750', textDecoration: 'none',
            fontFamily: t.fontMain, lineHeight: '18px',
          }}>{link}</a>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <a href="/auth/login" style={{ fontSize: 14, fontWeight: 400, color: '#5C5750', textDecoration: 'none', fontFamily: t.fontMain, lineHeight: '18px' }}>Sign in</a>
        <a href="/auth/register" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8, padding: '9px 18px',
          backgroundColor: '#C4622A', textDecoration: 'none',
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#FFFFFF', fontFamily: t.fontMain, lineHeight: '20px' }}>Get started →</span>
        </a>
      </div>
    </nav>
  )
}

// ═══════════════════════════════════════════════
// HERO SECTION
// ═══════════════════════════════════════════════

function HeroSection() {
  return (
    <section style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      width: '100%', paddingTop: 160, paddingBottom: 100, paddingLeft: 80, paddingRight: 80, gap: 32,
    }}>
      <span style={{
        fontSize: 12, fontWeight: 500, letterSpacing: '0.08em',
        color: t.textFaint, fontFamily: t.fontMain, lineHeight: '16px',
      }}>
        REAL ESTATE  ·  LEGAL  ·  FOUNDERS  ·  OPERATORS  ·  RESEARCH
      </span>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, maxWidth: 700 }}>
        <h1 style={{
          fontSize: 56, fontWeight: 700, letterSpacing: '-0.02em',
          lineHeight: '64px', textAlign: 'center', whiteSpace: 'pre-line',
          color: t.text, fontFamily: t.fontMain, margin: 0,
        }}>
          Your document workspace.{'\n'}From inbox to signed.
        </h1>
      </div>

      <p style={{
        fontSize: 16, fontWeight: 400, lineHeight: '26px',
        textAlign: 'center', color: t.textBody, fontFamily: t.fontMain,
        margin: 0, maxWidth: 520,
      }}>
        Documents arrive from Gmail, Slack, and Dropbox. You review, analyze, sign, and send — without switching apps.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <PrimaryButton href="/auth/register">Get started free →</PrimaryButton>
        <OutlineButton href="/#how-it-works">See how it works →</OutlineButton>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// IMAGE SECTIONS (exported PNGs)
// ═══════════════════════════════════════════════

function ImageSection({ src, alt, bgColor }: { src: string; alt: string; bgColor?: string }) {
  return (
    <section style={{
      display: 'flex', justifyContent: 'center', width: '100%',
      backgroundColor: bgColor || t.bg,
    }}>
      <img
        src={src}
        alt={alt}
        style={{ width: '100%', maxWidth: 1440, display: 'block' }}
        loading="lazy"
      />
    </section>
  )
}

// ═══════════════════════════════════════════════
// CLOSING CTA SECTION
// ═══════════════════════════════════════════════

function ClosingCTA() {
  return (
    <section style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      width: '100%', padding: '120px 80px 100px', gap: 28,
    }}>
      <h2 style={{
        fontSize: 42, fontWeight: 700, letterSpacing: '-0.02em',
        lineHeight: '50px', textAlign: 'center', whiteSpace: 'pre-line',
        color: t.text, fontFamily: t.fontMain, margin: 0, maxWidth: 700,
      }}>
        {'One workspace. Every document.\nNo switching.'}
      </h2>

      <p style={{
        fontSize: 16, fontWeight: 400, lineHeight: '26px',
        textAlign: 'center', color: t.textBody, fontFamily: t.fontMain,
        margin: 0, maxWidth: 480,
      }}>
        Real estate agents close deals faster. Lawyers review contracts in seconds. Founders move without friction.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <PrimaryButton href="/auth/register">Get started free →</PrimaryButton>
        <OutlineButton href="/auth/register">See all 58+ tools →</OutlineButton>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// THEME SWITCHER
// ═══════════════════════════════════════════════
type ThemeMode = 'auto' | 'light' | 'dark'

function ThemeSwitcher() {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'auto'
    const stored = localStorage.getItem('corner:theme')
    if (stored === 'light' || stored === 'auto' || stored === 'dark') return stored
    return 'auto'
  })

  const setMode = (m: ThemeMode) => {
    setModeState(m)
    setTheme(m)
  }

  const modes: ThemeMode[] = ['auto', 'light', 'dark']
  const activeIdx = modes.indexOf(mode)
  const indicatorOffset = activeIdx * 30
  const iconColor = (m: ThemeMode) => mode === m ? '#F5F2EC' : '#F5F2EC59'
  const btnStyle = { position: 'relative' as const, zIndex: 1, width: 28, height: 28, borderRadius: 100, border: 'none', cursor: 'pointer', backgroundColor: 'transparent', display: 'flex' as const, alignItems: 'center' as const, justifyContent: 'center' as const, padding: 0 }

  return (
    <div style={{
      position: 'relative',
      display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start',
      borderRadius: 100, padding: 3, gap: 2, marginTop: 4, width: 'fit-content',
      backgroundColor: t.bgDark, border: '1px solid #F5F2EC14',
    }}>
      <div style={{
        position: 'absolute', top: 3, left: 3 + indicatorOffset,
        width: 28, height: 28, borderRadius: 100,
        backgroundColor: '#3A2E24', boxShadow: '#00000080 0px 1px 3px',
        transition: 'left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: 'none',
      }} />
      <button onClick={() => setMode('auto')} aria-label="System theme" style={btnStyle}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect x="1" y="2" width="12" height="8" rx="1.5" stroke={iconColor('auto')} strokeWidth="1.2" fill="none" />
          <line x1="4" y1="12" x2="10" y2="12" stroke={iconColor('auto')} strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
      <button onClick={() => setMode('light')} aria-label="Light theme" style={btnStyle}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="3" stroke={iconColor('light')} strokeWidth="1.2" fill="none" />
          <line x1="7" y1="0.5" x2="7" y2="2" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
          <line x1="7" y1="12" x2="7" y2="13.5" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
          <line x1="0.5" y1="7" x2="2" y2="7" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
          <line x1="12" y1="7" x2="13.5" y2="7" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
      <button onClick={() => setMode('dark')} aria-label="Dark theme" style={btnStyle}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M12 8.5A5.5 5.5 0 1 1 5.5 2 4.5 4.5 0 0 0 12 8.5Z" stroke={iconColor('dark')} strokeWidth="1.2" fill="none" />
        </svg>
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════

function Footer() {
  const columns = [
    { title: 'Product', links: [
      { label: 'Features', href: '/features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'How it works', href: '/how-it-works' },
      { label: 'Changelog', href: '#' },
    ]},
    { title: 'Tools', links: [
      { label: 'PDF tools', href: '#' },
      { label: 'E-signature', href: '#' },
      { label: 'AI analysis', href: '#' },
      { label: 'File conversion', href: '#' },
    ]},
    { title: 'Company', links: [
      { label: 'About', href: '#' },
      { label: 'Blog', href: '#' },
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '#' },
    ]},
  ]

  return (
    <footer style={{
      width: '100%', padding: '56px 170px 40px',
      backgroundColor: t.bgDark,
      borderTop: '1px solid #F5F2EC14',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <CornerLogo color="#F5F2EC" size={22} />
            <p style={{ fontSize: 13, fontWeight: 400, lineHeight: '160%', color: '#F5F2EC59', maxWidth: 200, margin: 0, fontFamily: t.fontMain }}>
              Stop managing documents. Start finishing them.
            </p>
            <ThemeSwitcher />
          </div>
          <div style={{ display: 'flex', gap: 72 }}>
            {columns.map(col => (
              <div key={col.title} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: '#F5F2EC40', lineHeight: '14px' }}>{col.title}</span>
                {col.links.map(link => (
                  <a key={link.label} href={link.href} style={{ fontSize: 13, fontWeight: 400, color: '#F5F2EC80', textDecoration: 'none', lineHeight: '16px', fontFamily: t.fontMain }}>{link.label}</a>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 24, borderTop: '1px solid #F5F2EC0F' }}>
          <span style={{ fontSize: 12, fontWeight: 400, color: '#F5F2EC33', lineHeight: '16px', fontFamily: t.fontMain }}>© 2026 Corner. All rights reserved.</span>
          <span style={{ fontSize: 12, fontWeight: 400, color: '#F5F2EC33', lineHeight: '16px', fontFamily: t.fontMain }}>trycorner.co</span>
        </div>
      </div>
    </footer>
  )
}

// ═══════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════

export default function FeaturesPage() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      backgroundColor: t.bg, fontFamily: t.fontMain, minHeight: '100vh',
    }}>
      <Navbar />
      <ScrollReveal style={{ width: '100%' }}><HeroSection /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}>
        <ImageSection src="/FeaturScreen/IntegrationFlowSection@2x.png" alt="Documents flow in, decisions flow out — integration diagram showing Gmail, Slack, Dropbox connecting to Corner" />
      </ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}>
        <ImageSection src="/FeaturScreen/FeatureCardsSection@2x.png" alt="Everything inside the workspace — E-sign, Chat, Summarize, OCR, Conversion, Merge & Split" />
      </ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}>
        <ImageSection src="/FeaturScreen/WhoItsForSection@2x.png" alt="Built for people who live in documents — Real Estate, Law Firms, Medical Offices, Founders, Students" />
      </ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><ClosingCTA /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><Footer /></ScrollReveal>
    </div>
  )
}
